import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  FolderTree,
  MoreHorizontal,
  Pencil,
  Trash2,
  Package,
} from "lucide-react";
import { toast } from "sonner";
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useToggleCategory,
  useDeleteCategory,
  useCategoryIngredients,
  useSetCategoryIngredients,
} from "../api";
import { useIngredients } from "@/features/inventory/api";
import { usePagination } from "@/hooks";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "@/components/ui";
import {
  PageHeader,
  PageLoader,
  PageError,
  EmptyState,
  IngredientManager,
  DataTable,
  type Column,
} from "@/components/common";
import type {
  Category,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from "@/types";

export function CategoriesPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );
  const [ingredientCategoryId, setIngredientCategoryId] = useState<
    string | null
  >(null);

  const {
    data: categories,
    isLoading,
    error,
    refetch,
  } = useCategories({ all: true });
  const { data: ingredientsData } = useIngredients({ limit: 100 });
  const ingredientsList = ingredientsData?.data ?? [];
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const toggleCategory = useToggleCategory();
  const deleteCategory = useDeleteCategory();

  const filteredCategories = categories?.filter((cat) => {
    const matchesSearch =
      !search || cat.name.toLowerCase().includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && cat.isActive) ||
      (statusFilter === "inactive" && !cat.isActive);

    return matchesSearch && matchesStatus;
  });

  const { page, pageSize, setPage, setPageSize, paginatedData, total } =
    usePagination(filteredCategories);

  const handleCreate = async (data: CreateCategoryRequest) => {
    try {
      await createCategory.mutateAsync(data);
      toast.success("Category created");
      setIsCreateOpen(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to create category",
      );
    }
  };

  const handleUpdate = async (data: UpdateCategoryRequest) => {
    if (!selectedCategory) return;
    try {
      await updateCategory.mutateAsync({ id: selectedCategory.id, data });
      toast.success("Category updated");
      setSelectedCategory(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update category",
      );
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await toggleCategory.mutateAsync(id);
      toast.success("Category status updated");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update category",
      );
    }
  };

  const handleDelete = async (id: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this category? Products using this category will need to be reassigned.",
      )
    )

      return;
    try {
      await deleteCategory.mutateAsync(id);
      toast.success("Category deleted");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete category",
      );
    }
  };

  const columns: Column<Category>[] = [
    {
      key: "name",
      header: "Name",
      cell: (category) => (
        <span className="font-medium">{category.name}</span>
      ),
    },
    {
      key: "parent",
      header: "Parent",
      cell: (category) => {
        const parent = categories?.find((c) => c.id === category.parentId);

        return parent ? (
          <Badge variant="outline">{parent.name}</Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      key: "sortOrder",
      header: "Sort Order",
      cell: (category) => category.sortOrder,
    },
    {
      key: "status",
      header: "Status",
      cell: (category) => (
        <Switch
          checked={category.isActive}
          onCheckedChange={() => handleToggle(category.id)}
        />
      ),
    },
    {
      key: "actions",
      header: "Actions",
      className: "w-[100px]",
      cell: (category) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setSelectedCategory(category)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setIngredientCategoryId(category.id)}
            >
              <Package className="mr-2 h-4 w-4" />
              Default Ingredients
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => handleDelete(category.id)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const renderMobileCard = (category: Category) => {
    const parent = categories?.find((c) => c.id === category.parentId);

    return (
      <div className="rounded-lg border p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-medium">{category.name}</p>
            {parent && (
              <p className="text-sm text-muted-foreground">
                Parent: {parent.name}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              Sort: {category.sortOrder}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={category.isActive}
              onCheckedChange={() => handleToggle(category.id)}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSelectedCategory(category)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIngredientCategoryId(category.id)}
                >
                  <Package className="mr-2 h-4 w-4" />
                  Default Ingredients
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => handleDelete(category.id)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) return <PageLoader />;
  if (error)

    return <PageError message="Failed to load categories" onRetry={refetch} />;

  return (
    <div className="container mx-auto p-6">
      <PageHeader
        title="Categories"
        description="Organize products into categories"
        actions={
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Category
          </Button>
        }
      />
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2">
              <FolderTree className="h-5 w-5" />
              Categories ({filteredCategories?.length || 0})
            </CardTitle>
            <div className="flex gap-2">
              <div className="relative w-64">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search categories..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={(v) =>
                  setStatusFilter(v as "all" | "active" | "inactive")
                }
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!filteredCategories?.length ? (
            <EmptyState
              icon={FolderTree}
              title="No categories found"
              description={
                search
                  ? "Try adjusting your search"
                  : "Get started by adding a category"
              }
              action={
                !search && (
                  <Button onClick={() => setIsCreateOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Category
                  </Button>
                )
              }
            />
          ) : (
            <DataTable
              data={paginatedData}
              columns={columns}
              rowKey={(category) => category.id}
              renderMobileCard={renderMobileCard}
              pagination={{
                page,
                pageSize,
                total,
                onPageChange: setPage,
                onPageSizeChange: setPageSize,
              }}
            />
          )}
        </CardContent>
      </Card>
      <CategoryFormDialog
        key="create"
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSubmit={
          handleCreate as (
            data: CreateCategoryRequest | UpdateCategoryRequest,
          ) => void
        }
        isLoading={createCategory.isPending}
        categories={categories || []}
        title="Create Category"
      />
      {selectedCategory && (
        <CategoryFormDialog
          key={selectedCategory.id}
          open={!!selectedCategory}
          onOpenChange={(open) => !open && setSelectedCategory(null)}
          category={selectedCategory}
          onSubmit={
            handleUpdate as (
              data: CreateCategoryRequest | UpdateCategoryRequest,
            ) => void
          }
          isLoading={updateCategory.isPending}
          categories={
            categories?.filter((c) => c.id !== selectedCategory.id) || []
          }
          title="Edit Category"
        />
      )}

      {ingredientCategoryId && (
        <CategoryIngredientsDialog
          categoryId={ingredientCategoryId}
          categoryName={
            categories?.find((c) => c.id === ingredientCategoryId)?.name || ""
          }
          availableIngredients={ingredientsList}
          open={!!ingredientCategoryId}
          onOpenChange={(open) => !open && setIngredientCategoryId(null)}
        />
      )}
    </div>
  );
}

interface CategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category?: Category;
  onSubmit: (data: CreateCategoryRequest | UpdateCategoryRequest) => void;
  isLoading: boolean;
  categories: Category[];
  title: string;
}

function CategoryFormDialog({
  open,
  onOpenChange,
  category,
  onSubmit,
  isLoading,
  categories,
  title,
}: CategoryFormDialogProps) {
  const [formData, setFormData] = useState({
    name: category?.name || "",
    parentId: category?.parentId || "",
    sortOrder: category?.sortOrder ?? 0,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: formData.name,
      parentId: formData.parentId || undefined,
      sortOrder: formData.sortOrder,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Hot Drinks, Pastries"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="parentId">Parent Category</Label>
            <Select
              value={formData.parentId || "none"}
              onValueChange={(v) =>
                setFormData({ ...formData, parentId: v === "none" ? "" : v })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select parent category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Parent (Top Level)</SelectItem>
                {categories
                  .filter((c) => c.isActive)
                  .map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Nest this category under another for hierarchical organization
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="sortOrder">Sort Order</Label>
            <Input
              id="sortOrder"
              type="number"
              value={formData.sortOrder || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  sortOrder: parseInt(e.target.value) || 0,
                })
              }
              placeholder="e.g., 0"
            />
            <p className="text-xs text-muted-foreground">
              Lower numbers appear first in the list
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface CategoryIngredientsDialogProps {
  categoryId: string;
  categoryName: string;
  availableIngredients: { id: string; name: string; unit: string }[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CategoryIngredientsDialog({
  categoryId,
  categoryName,
  availableIngredients,
  open,
  onOpenChange,
}: CategoryIngredientsDialogProps) {
  const { data: existingIngredients, isLoading } =
    useCategoryIngredients(categoryId);
  const setCategoryIngredients = useSetCategoryIngredients();

  const [ingredients, setIngredients] = useState<
    {
      ingredientId: string;
      quantityUsed: number;
      variantNamePattern?: string;
    }[]
  >([]);

  useEffect(() => {
    if (existingIngredients) {
      setIngredients(
        existingIngredients.map((i) => ({
          ingredientId: i.ingredientId,
          quantityUsed: i.quantityUsed,
          variantNamePattern: i.variantNamePattern ?? undefined,
        })),
      );
    }
  }, [existingIngredients]);

  const handleSave = async () => {
    const invalid = ingredients.find(
      (i) => !i.quantityUsed || i.quantityUsed < 1,
    );
    if (invalid) {
      toast.error("Each ingredient quantity must be at least 1");

      return;
    }
    try {
      await setCategoryIngredients.mutateAsync({
        categoryId,
        data: { ingredients },
      });
      toast.success("Default ingredients saved");
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save ingredients",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Default Ingredients for "{categoryName}"</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="mb-4 text-sm text-muted-foreground">
            These ingredients will be automatically deducted from inventory when
            any product in this category is ordered. Use the variant pattern to
            match specific sizes (e.g., "Small" only applies to variants
            containing "Small").
          </p>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <IngredientManager
              ingredients={ingredients}
              availableIngredients={availableIngredients}
              onChange={setIngredients}
              title=""
              description=""
              compact
              showVariantPattern
            />
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={setCategoryIngredients.isPending}
          >
            {setCategoryIngredients.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
